import React, { ComponentType } from 'react';
import { Article } from '../../../../components/Article';
import { Column, Row } from '../../../../components/Grid';
import { Image, ImageZoom } from '../../../../components/Image';
import { ContentHeader } from '../../../../components/layout/ContentHeader';
import { HtmlContent } from '../../../../components/layout/HtmlContent';
import { Section } from '../../../../components/Section';
import { TypoImage } from '../../../../components/TypoImage';
import VideoContainer from '../../../../components/TypoVideo';
import { selectDoktype } from '../../../../provider/Typo3Provider/selectors';
import {
  CType,
  TextAlign,
  Typo3Files
} from '../../../../provider/Typo3Provider/types';
import {
  withTypo3,
  WithTypo3Props
} from '../../../../provider/Typo3Provider/withTypo3';

import styled from 'styled-components';
import {
  withBreakpoints,
  WithBreakpointsProps
} from '../../../../provider/BreakpointProvider/withBreakpoints';
import { ModalContext } from '../../../../provider/ModalProvider';
import { defaults } from '../../../../theme/base/defaults';

const Content = styled.div<{align?: TextAlign, mobileAlign?: TextAlign}>`
  text-align: ${(props) => props.mobileAlign || 'center'};

  @media (min-width: ${defaults.breakpoints.large}px) {
    text-align: ${(props) => props.align || 'center'};
  }

  @media print {
    text-align: left;

    ${Column} {
      max-width: inherit;
      flex: inherit;
    }
  }
`;

const Text = ({
  files = { image: [] },
  bodytext = '',
  header,
  subheader,
  header_position,
  bodytext_position,
  bodytext_position_mobile,
  typo3
}: CType & WithTypo3Props) => {
  const doktype = selectDoktype(typo3);
  const images = files.image || [];

  return (
    <Article>
      <Section fullWidthSectionHeader>
        <Content
          align={bodytext_position}
          mobileAlign={bodytext_position_mobile}
        >
          <Row justifyContent="center">
            <Column large={8}>
              <ContentHeader
                header={header}
                subheader={subheader}
                doktype={doktype ? doktype : 'default'}
                align={header_position}
              />

              {images.map((image, index) => (
                <TypoImage key={index} image={image} />
              ))}
              <HtmlContent htmlString={bodytext} />
            </Column>
          </Row>
        </Content>
      </Section>
    </Article>
  );
};

const TextWithTypo3 = withTypo3(Text);

export { TextWithTypo3 as Text };

interface MediaComponentProps {
  file: Typo3Files;
}

const MediaImage = ({
  file = { image: [] },
  breakpoints
}: MediaComponentProps & WithBreakpointsProps) => {
  const [imageFile = null] = file.image || [];
  const { medium } = breakpoints;
  if (medium && imageFile && imageFile.imageZoomUri) {
    return (
      <ModalContext.Consumer>
        {({ showModal }) => (
          <ImageZoom
            onClick={() => {
              showModal(<Image src={imageFile.imageZoomUri} />, {
                zoom: true
              });
            }}
          >
            <TypoImage image={imageFile} />
          </ImageZoom>
        )}
      </ModalContext.Consumer>
    );
  } else {
    return <TypoImage image={imageFile} />;
  }
};

// TODO: implement Video-Component
const MediaVideo = ({ file }: MediaComponentProps) => (
  <VideoContainer file={file} />
);

const mkTextMedia = (
  columnWidth: number,
  side: 'left' | 'right',
  MediaComponent: ComponentType<MediaComponentProps>
) => {

  const TextPicComponent = ({
    files = { image: [], assets: [] },
    bodytext = '',
    header,
    subheader,
    typo3,
    content_element_width
  }: CType & WithTypo3Props) => {
    const doktype = selectDoktype(typo3);

    const leftMedium = side === 'left' ? columnWidth : 12 - columnWidth;
    const rightMedium = side === 'right' ? columnWidth : 12 - columnWidth;

    const maxLargeColumns = content_element_width === 'text-width' ? 8 : 12;
    const largeTextColumns = Math.round(columnWidth / 12 * maxLargeColumns);

    const leftLarge = side === 'left' ? largeTextColumns : maxLargeColumns - largeTextColumns;
    const rightLarge = side === 'right' ? largeTextColumns : maxLargeColumns - largeTextColumns;

    return (
      <Article textpic>
        <Section>
          <ContentHeader
            header={header}
            subheader={subheader}
            doktype={doktype ? doktype : 'default'}
          />
          <Row horizontalAlignment='center'>
            <Column
              small={12}
              medium={leftMedium}
              large={leftLarge}
              orderOnSmall={side === 'left' ? 1 : 2}
            >
              <MediaComponent file={files} />
            </Column>
            <Column
              small={12}
              medium={rightMedium}
              large={rightLarge}
              orderOnSmall={side === 'right' ? 1 : 2}
            >
              <HtmlContent htmlString={bodytext} />
            </Column>
          </Row>
        </Section>
      </Article>
    );
  };
  TextPicComponent.displayName = `TextPic${side
    .charAt(0)
    .toUpperCase()}${side.substr(1).toLowerCase()}${(
    (12 / columnWidth) *
    100
  ).toFixed(0)}`;
  return withTypo3(TextPicComponent);
};

export const TextPicLeft33 = mkTextMedia(
  4,
  'left',
  withBreakpoints(MediaImage)
);

export const TextPicRight33 = mkTextMedia(
  4,
  'right',
  withBreakpoints(MediaImage)
);

export const TextPicLeft50 = mkTextMedia(
  6,
  'left',
  withBreakpoints(MediaImage)
);

export const TextPicRight50 = mkTextMedia(
  6,
  'right',
  withBreakpoints(MediaImage)
);

export const TextVideoLeft50 = mkTextMedia(6, 'left', MediaVideo);

export const TextVideoRight50 = mkTextMedia(6, 'right', MediaVideo);
